import { CollapseDrawerProvider } from "src/components/core/mui/contexts/CollapseDrawerContext";
import { SettingsProvider } from "src/components/core/mui/contexts/SettingsContext";
import ProgressBar from "src/components/core/mui/components/ProgressBar";
import type {
  SettingsValueProps,
  ThemeMode,
} from "src/components/core/mui/components/settings/type";
import ThemeProvider from "src/components/core/mui/theme";
import { getSettings } from "src/components/core/mui/utils/getSettings";
import "../styles/globals.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import axios from "axios";
import { getCookie, getCookies } from "cookies-next";

import { NextPage } from "next";
import App, { AppContext, AppProps } from "next/app";
import Head from "next/head";
import { adminChromeInter } from "src/styles/admin-chrome-font";
import { ReactElement, ReactNode, useEffect } from "react";
import "react-lazy-load-image-component/src/effects/black-and-white.css";
import "react-lazy-load-image-component/src/effects/blur.css";
import "react-lazy-load-image-component/src/effects/opacity.css";
import "simplebar/src/simplebar.css";
import NonSSRWrapper from "src/components/common/NonSSRWrapper";

import CustomThemeProvider from "src/context/theme-context";
import { ThemeTypes } from "src/store/themeStore/props";
import NotificationToast from "src/components/notifications/toasty";
import { CacheProvider } from "@emotion/react";
import createEmotionCache from "src/lib/createEmotionCache";
import { usePageView } from "src/hooks/usePostHogPageView";
import { clearProductSessions } from "src/lib/auth-central/browser";
import { installUnauthorizedResponseInterceptor } from "src/lib/auth-central/unauthorized-session";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

interface MyAppProps extends AppProps {
  settings: SettingsValueProps;
  Component: NextPageWithLayout;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      cacheTime: 1000 * 60 * 15,
    },
  },
});
const clientSideEmotionCache = createEmotionCache();

export default function MyApp(props: MyAppProps) {
  const { Component, pageProps } = props;
  const getLayout = Component.getLayout ?? ((page) => page);
  const theme = getCookie("@sync-theme") ?? ThemeTypes.Light;
  const authToken = getCookie("app-sync-auth-token")?.toString();
  usePageView();

  useEffect(() => {
    if (!authToken) {
      return;
    }

    return installUnauthorizedResponseInterceptor(axios, {
      clearSession: clearProductSessions,
      getReturnTo: () =>
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      redirect: (path) => window.location.replace(path),
    });
  }, [authToken]);

  useEffect(() => {
    // eslint-disable-next-line turbo/no-undeclared-env-vars
    const isDevelopment = process.env.NODE_ENV === "development";
    const shouldDisableServiceWorker =
      process.env.NEXT_PUBLIC_DISABLE_SERVICE_WORKER === "true";

    if (
      (!isDevelopment && !shouldDisableServiceWorker) ||
      !("serviceWorker" in navigator)
    ) {
      return;
    }

    const cleanupServiceWorker = async () => {
      const hadController = Boolean(navigator.serviceWorker.controller);
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration) => registration.unregister()),
      );

      if ("caches" in window) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((key) => window.caches.delete(key)));
      }

      if (hadController && !sessionStorage.getItem("app-sync-sw-cleaned")) {
        sessionStorage.setItem("app-sync-sw-cleaned", "1");
        window.location.reload();
      }
    };

    cleanupServiceWorker().catch(() => undefined);
  }, []);

  return (
    <div className={adminChromeInter.variable}>
      <NonSSRWrapper>
        <CacheProvider value={clientSideEmotionCache}>
          <QueryClientProvider client={queryClient}>
            <Head>
              <meta
                name="viewport"
                content="initial-scale=1, width=device-width"
              />
            </Head>
            <SettingsProvider
              defaultSettings={{
                themeColorPresets: "blue",
                themeContrast: "default",
                themeDirection: "ltr",
                themeLayout: "horizontal",
                themeMode: theme as ThemeMode,
                themeStretch: false,
              }}
            >
              <ThemeProvider>
                <CustomThemeProvider>
                  {/* <PostHogProvider client={posthog}> */}
                  <CollapseDrawerProvider>
                    <CustomThemeProvider>
                      {/* <MotionLazyContainer> */}
                      <NotificationToast />
                      <ProgressBar />
                      {getLayout(<Component {...pageProps} />)}
                      {/* </MotionLazyContainer> */}
                    </CustomThemeProvider>
                  </CollapseDrawerProvider>
                  {/* </PostHogProvider> */}
                </CustomThemeProvider>
              </ThemeProvider>
            </SettingsProvider>
            <ReactQueryDevtools
              initialIsOpen={false}
              toggleButtonProps={{
                style: {
                  left: "var(--admin-devtools-left-offset, 0px)",
                },
              }}
            />
          </QueryClientProvider>
        </CacheProvider>
      </NonSSRWrapper>
    </div>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);

  const cookies = context.ctx.req
    ? getCookies({ req: context.ctx.req, res: context.ctx.res })
    : getCookies();

  const settings = getSettings(cookies);

  return {
    ...appProps,
    settings,
  };
};
